<script setup lang="ts">
import { Clock, Loader2, MapPin, MessageSquareReply, Receipt, Sparkles, Star } from 'lucide-vue-next'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { DeliveryReview } from '@/types/deliveryReviews'
import { DELIVERY_PLATFORM_LABELS, NEGATIVE_RATING_THRESHOLD, REPLY_DEADLINE_DAYS } from '@/types/deliveryReviews'
import { apiErrorMessage, replyToReview, suggestReply } from '../deliveryReviewsApi'

const props = defineProps<{
  merchantId: number
  review: DeliveryReview
}>()

const emit = defineEmits<{
  replied: [review: DeliveryReview]
}>()

const MAX_RATING = 5
const DAY_MS = 24 * 60 * 60 * 1000

const { t, locale } = useI18n()
const notification = useNotification()

const isReplying = ref(false)
const replyText = ref('')
const isSuggesting = ref(false)
const isPublishing = ref(false)

const platform = computed(() => props.review.reviewManager?.slug ?? null)
const isAnswered = computed(() => props.review.ownerResponseText !== null || props.review.repliedAt !== null)
const isNegative = computed(() => props.review.rating !== null && props.review.rating <= NEGATIVE_RATING_THRESHOLD)

const replyDeadline = computed(() =>
  platform.value ? new Date(new Date(props.review.date).getTime() + REPLY_DEADLINE_DAYS[platform.value] * DAY_MS) : null
)
const canReply = computed(() => !isAnswered.value && replyDeadline.value !== null && replyDeadline.value > new Date())
const daysLeft = computed(() =>
  replyDeadline.value ? Math.ceil((replyDeadline.value.getTime() - Date.now()) / DAY_MS) : 0
)

const replyStatusColor = computed<ChipColor>(() => {
  const status = props.review.ownerResponseStatus
  if (status === 'REJECTED') return 'red'
  if (status === 'SENT' || status === 'APPROVED') return 'green'
  return 'blue'
})

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString(locale.value, { day: 'numeric', month: 'short', year: 'numeric' })

const formattedTotal = computed(() => {
  const order = props.review.order
  if (!order || order.orderTotal === null) return null
  return new Intl.NumberFormat(locale.value, { style: 'currency', currency: order.currencyCode ?? 'EUR' }).format(
    order.orderTotal
  )
})

function cancel() {
  isReplying.value = false
  replyText.value = ''
}

async function suggest() {
  isSuggesting.value = true
  try {
    replyText.value = await suggestReply(props.merchantId, props.review.id)
  } catch {
    notification.create('error', { text: t('pages.deliveryReviews.reply.suggestError') })
  } finally {
    isSuggesting.value = false
  }
}

async function publish() {
  if (!replyText.value.trim()) return
  isPublishing.value = true
  try {
    emit('replied', await replyToReview(props.merchantId, props.review.id, replyText.value.trim()))
    notification.create('success', { text: t('pages.deliveryReviews.reply.published') })
    cancel()
  } catch (error) {
    notification.create('error', { text: apiErrorMessage(error) ?? t('pages.deliveryReviews.reply.error') })
  } finally {
    isPublishing.value = false
  }
}
</script>

<template>
  <Card class="flex flex-col gap-3 p-5">
    <div class="flex flex-wrap items-center gap-2">
      <BaseChip v-if="platform" color="violet" :text="DELIVERY_PLATFORM_LABELS[platform]" />
      <span v-if="review.store" class="flex items-center gap-1 text-sm text-muted-foreground">
        <MapPin class="size-4" />{{ review.store.name }}
      </span>
      <span class="ml-auto text-sm text-muted-foreground">{{ formatDate(review.date) }}</span>
    </div>

    <div class="flex items-center gap-2">
      <div
        v-if="review.rating !== null"
        class="flex items-center gap-0.5"
        :aria-label="t('pages.deliveryReviews.reviews.rating', { rating: review.rating })"
      >
        <Star
          v-for="index in MAX_RATING"
          :key="index"
          :class="[
            'size-4',
            index <= (review.rating ?? 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'
          ]"
        />
      </div>
      <BaseChip v-if="isNegative" color="red" :text="t('pages.deliveryReviews.reviews.negative')" />
      <span class="text-sm font-medium">{{ review.name ?? t('pages.deliveryReviews.reviews.anonymous') }}</span>
    </div>

    <p v-if="review.text" class="whitespace-pre-line text-sm">{{ review.text }}</p>
    <p v-else class="text-sm italic text-muted-foreground">{{ t('pages.deliveryReviews.reviews.noComment') }}</p>

    <div v-if="review.order" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span v-if="formattedTotal" class="flex items-center gap-1"
        ><Receipt class="size-3.5" />{{ formattedTotal }}</span
      >
      <span v-if="review.order.items?.length">
        {{ review.order.items.map((item) => `${item.quantity}× ${item.name}`).join(', ') }}
      </span>
    </div>

    <div v-if="isAnswered" class="flex flex-col gap-2 rounded-xl bg-muted p-3">
      <div class="flex items-center gap-2">
        <MessageSquareReply class="size-4 text-muted-foreground" />
        <span class="text-sm font-medium">{{ t('pages.deliveryReviews.reply.yourReply') }}</span>
        <BaseChip
          v-if="review.ownerResponseStatus"
          :color="replyStatusColor"
          :text="t(`pages.deliveryReviews.reply.status.${review.ownerResponseStatus}`, review.ownerResponseStatus)"
        />
      </div>
      <p class="whitespace-pre-line text-sm">
        {{ review.ownerResponseText ?? t('pages.deliveryReviews.reply.answeredOnPlatform') }}
      </p>
    </div>

    <template v-else-if="canReply">
      <div v-if="isReplying" class="flex flex-col gap-2">
        <Textarea
          v-model="replyText"
          :placeholder="t('pages.deliveryReviews.reply.placeholder')"
          :maxlength="2000"
          rows="4"
        />
        <BaseTip severity="warn" :text="t('pages.deliveryReviews.reply.irreversible')" />
        <div class="flex flex-wrap justify-end gap-2">
          <Button variant="ghost-primary" :disabled="isSuggesting || isPublishing" @click="suggest">
            <Loader2 v-if="isSuggesting" class="animate-spin" />
            <Sparkles v-else />
            {{ t('pages.deliveryReviews.reply.suggest') }}
          </Button>
          <Button variant="outline" :disabled="isPublishing" @click="cancel">
            {{ t('pages.deliveryReviews.reply.cancel') }}
          </Button>
          <Button variant="default-primary" :disabled="!replyText.trim() || isPublishing" @click="publish">
            <Loader2 v-if="isPublishing" class="animate-spin" />
            {{ t('pages.deliveryReviews.reply.publish') }}
          </Button>
        </div>
      </div>
      <div v-else class="flex items-center justify-between gap-3">
        <span class="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock class="size-3.5" />
          {{ t('pages.deliveryReviews.reply.daysLeft', { count: daysLeft }, daysLeft) }}
        </span>
        <Button variant="outline" size="sm" @click="isReplying = true">
          <MessageSquareReply />
          {{ t('pages.deliveryReviews.reply.reply') }}
        </Button>
      </div>
    </template>

    <p v-else class="text-xs text-muted-foreground">{{ t('pages.deliveryReviews.reply.expired') }}</p>
  </Card>
</template>
